import { useState, useEffect, useRef, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import HBMediaElement from '@honeybook-ui/HBMediaElement/HBMediaElement';
import FeatureBlockSection from '../../FeatureBlockElements/FeatureBlockSection/FeatureBlockSection';
import FeatureBlockContent from '../../FeatureBlockElements/FeatureBlockContent/FeatureBlockContent';
import NavigateLink from '../../Link/NavigateLink';
import ImageTextElement from '../../ImageTextElement/ImageTextElement';
import AnalyticsService, {
    AnalyticsEvents
} from '../../../Services/AnalyticsService';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import HBIcon from '@honeybook-ui/HBIcon/HBIcon';
import HBText from '@honeybook-ui/HBText/HBText';
import Collapse from '../../../vendor/blueprintjs/collapse';
import RegistrationForm from '../../RegistrationForm/RegistrationForm';

import InvoicesIcon from '../../../svg/features/invoices-icon.inline.svg';
import ContractsIcon from '../../../svg/features/contracts-icon.inline.svg';
import AIIcon from '../../../svg/features/lightning.inline.svg';
import PaymentsIcon from '../../../svg/features/payments-icon.inline.svg';
import SchedulingIcon from '../../../svg/features/scheduling-icon.inline.svg';

import './feature-value-props-block.scss';

const BOX_LINK_CLASS = 'feature-value-props__box-link';
const IMAGE_WIDTH_DESKTOP = 880;
const IMAGE_WIDTH_MOBILE = 450;

const defaultValues = [
    {
        id: 'invoices',
        icon: <InvoicesIcon />,
        imagePath: 'marketing_site/home_page/invoices.png',
        link: '/online-invoices'
    },
    {
        id: 'contracts',
        icon: <ContractsIcon />,
        imagePath: 'marketing_site/home_page/contracts.png',
        link: '/online-contract'
    },
    {
        id: 'payments',
        icon: <PaymentsIcon />,
        imagePath: 'marketing_site/home_page/payments.png',
        link: '/online-payment-software'
    },
    {
        id: 'scheduling',
        icon: <SchedulingIcon />,
        imagePath: 'marketing_site/home_page/scheduling.png',
        link: '/meeting-scheduler'
    },
    {
        id: 'ai',
        icon: <AIIcon />,
        imagePath: 'marketing_site/home_page/AIcomposer.png',
        link: '/product/ai'
    }
];

const FeatureValuePropsBlock = ({
    values = defaultValues,
    hideCta,
    title,
    titleTop,
    minimal
}) => {
    const { t } = useTranslation();
    const breakpoints = useBreakpoint();

    const [selectedItemIndex, setSelectedItemIndex] = useState(0);
    const [autoTransition, setAutoTransition] = useState(true);
    const previousSelectedItemIndex = useRef();
    const selectorContainerRef = useRef();
    const timeoutRef = useRef();
    const [selectorDistanceFromTop, setSelectorDistanceFromTop] = useState(0);

    const isMobile = !breakpoints.largeUp;

    const updateSelectedElement = elementIndex => {
        setSelectedItemIndex(elementIndex);
        previousSelectedItemIndex.current = selectedItemIndex;
        if (!isMobile) {
            const selectedElement =
                selectorContainerRef.current.querySelectorAll(
                    `.${BOX_LINK_CLASS}`
                )[elementIndex];
            setSelectorDistanceFromTop(selectedElement.offsetTop);
        }
    };

    const onTrigger = elementIndex => {
        clearTimeout(timeoutRef.current);
        updateSelectedElement(elementIndex);
        setAutoTransition(false);
    };

    const continueTransition = () => {
        timeoutRef.current = setTimeout(() => {
            setAutoTransition(true);
        }, 150);
    };

    useEffect(() => {
        if (!isMobile) {
            const interval = setInterval(() => {
                if (!autoTransition) {
                    return clearInterval(interval);
                }
                if (selectedItemIndex < values.length - 1) {
                    updateSelectedElement(selectedItemIndex + 1);
                } else {
                    updateSelectedElement(0);
                }
            }, 2500);
            return () => clearInterval(interval);
        }
    }, [autoTransition, selectedItemIndex, breakpoints]);

    useEffect(() => {
        return () => clearTimeout(timeoutRef.current);
    }, []);

    const selectedItemIndicator = (
        <div
            className="feature-value-props__selector"
            style={{
                transform: `translateY(${selectorDistanceFromTop}px`
            }}
        >
            {values[0].link && (
                <div className="feature-value-props__arrow">
                    <HBMediaElement
                        name="marketing_site/arrow-right-24.svg"
                        alt="Right Arrow"
                        forceImgTag
                    />
                </div>
            )}
        </div>
    );

    const titleContent = title ? (
        <HBText
            customClass="feature-value-props__title"
            tag="h2"
            type="title-m"
        >
            {title}
        </HBText>
    ) : (
        ''
    );

    const desktopLayout = (
        <div className="feature-value-props__container">
            <FeatureBlockContent customClass="feature-value-props__text-content">
                {title && !titleTop && titleContent}
                <div
                    className="features-container"
                    ref={selectorContainerRef}
                    onClick={() =>
                        AnalyticsService.trackClick(
                            AnalyticsEvents.feature_values_block_link,
                            {
                                source: values[selectedItemIndex].id
                            }
                        )
                    }
                >
                    {values[0].link ? (
                        <a href={values[selectedItemIndex].link}>
                            {selectedItemIndicator}
                        </a>
                    ) : (
                        selectedItemIndicator
                    )}
                    {values.map(({ id, link, icon }, index) => {
                        const boxContent = (
                            <div
                                key={id}
                                className={`${BOX_LINK_CLASS} ${BOX_LINK_CLASS}--${id}`}
                                onMouseEnter={() => onTrigger(index)}
                                onMouseLeave={continueTransition}
                            >
                                <ImageTextElement
                                    customClass={`feature-value-props__box ${
                                        index === selectedItemIndex &&
                                        'feature-value-props__box--active'
                                    } ${
                                        selectedItemIndex <
                                            previousSelectedItemIndex.current &&
                                        'feature-value-props__box--down'
                                    }`}
                                    imagePosition="left"
                                    svg={icon}
                                    mediaFlags={{ forceImgTag: true }}
                                    title={t(
                                        `featureValueProps.props.${id}.title`
                                    )}
                                    description={t(
                                        `featureValueProps.props.${id}.description`
                                    )}
                                />
                            </div>
                        );
                        return link ? (
                            <NavigateLink
                                source="feature_values"
                                key={id}
                                link={link}
                                onClick={() =>
                                    AnalyticsService.trackClick(
                                        AnalyticsEvents.feature_values_block_link,
                                        {
                                            source: id
                                        }
                                    )
                                }
                            >
                                {boxContent}
                            </NavigateLink>
                        ) : (
                            boxContent
                        );
                    })}
                </div>
            </FeatureBlockContent>
            <FeatureBlockContent customClass="feature-value-props__media">
                <div className="feature-value-props__media-container">
                    {values.map(({ id, imagePath }, index) => (
                        <Fragment key={id}>
                            <HBMediaElement
                                customClass={`feature-value-props__media-img ${
                                    index == selectedItemIndex &&
                                    'feature-value-props__media-img--active'
                                }`}
                                key={id}
                                name={imagePath}
                                lazyLoad={false}
                                alt={t(`featureValueProps.props.${id}.alt`)}
                                width={IMAGE_WIDTH_DESKTOP}
                            />
                        </Fragment>
                    ))}
                </div>
            </FeatureBlockContent>
        </div>
    );

    const [openAccordionId, setOpenAccordionId] = useState(0);

    const handleAccordionClick = index => {
        AnalyticsService.trackClick(
            AnalyticsEvents.feature_value_accordion_toggle,
            {
                source: values[index].id
            }
        );
        index !== openAccordionId
            ? setOpenAccordionId(index)
            : setOpenAccordionId(null);
    };

    const mobileLayout = (
        <div className="feature-value-props-mweb">
            {title && !titleTop && titleContent}
            {values.map(({ id, imagePath, link, icon }, index) => (
                <div
                    key={id}
                    id={`feature-value-props-mweb__value--${index}`}
                    className={`feature-value-props-mweb__value ${
                        index === openAccordionId &&
                        'feature-value-props-mweb__value--active'
                    } ${link && 'feature-value-props-mweb__value--link'}`}
                    onClick={() => handleAccordionClick(index)}
                >
                    <HBIcon
                        customClass="feature-value-props-mweb__value__arrow"
                        name="nx-arrow-down-16"
                    />
                    <span className='class="media-element svg-content feature-value-props-mweb__value__icon'>
                        {icon}
                    </span>
                    <div className="feature-value-props-mweb__value__text-container">
                        <h3 className="feature-value-props-mweb__value__title">
                            {t(`featureValueProps.props.${id}.title`)}
                        </h3>
                        <p className="feature-value-props-mweb__value__description">
                            {t(`featureValueProps.props.${id}.description`)}
                        </p>
                    </div>
                    <Collapse
                        isOpen={index === openAccordionId}
                        transitionDuration={400}
                    >
                        <div className="feature-value-props-mweb__value__drawer">
                            <HBMediaElement
                                name={imagePath}
                                alt={t(`featureValueProps.props.${id}.alt`)}
                                width={IMAGE_WIDTH_MOBILE}
                            />
                            {link && (
                                <NavigateLink
                                    source="feature_values"
                                    link={link}
                                    className="hbui-button hbui-button--primary hbui-button--medium"
                                    onClick={() =>
                                        AnalyticsService.trackClick(
                                            AnalyticsEvents.feature_values_block_link,
                                            {
                                                source: values[index].id
                                            }
                                        )
                                    }
                                >
                                    {t('featureValueProps.buttonText')}{' '}
                                    {t(
                                        `featureValueProps.props.${id}.buttonText`
                                    )}{' '}
                                    <HBIcon name="nx-arrow-right-16" />
                                </NavigateLink>
                            )}
                        </div>
                    </Collapse>
                </div>
            ))}
        </div>
    );

    return (
        <div
            className={`feature-value-props ${
                minimal ? 'feature-value-props--minimal' : ''
            }`}
            id="feature-value-props"
        >
            <FeatureBlockSection halfSize lightBackground>
                {title && titleTop && (
                    <FeatureBlockContent customClass="feature-value-props__title-top">
                        {titleContent}
                    </FeatureBlockContent>
                )}
                {!isMobile ? desktopLayout : mobileLayout}
                {!hideCta && (
                    <FeatureBlockContent>
                        <RegistrationForm
                            size="large"
                            source="feature value props block"
                            direction="row"
                            showNoCcRequired
                        />
                    </FeatureBlockContent>
                )}
            </FeatureBlockSection>
        </div>
    );
};

export default FeatureValuePropsBlock;
